/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './Beneficiaries.scss';

import { isEmpty } from 'lodash';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import LegalCopy from 'components/shared/LegalCopy/LegalCopy';
import PreferencesHeader from 'components/layouts/Preferences/PreferencesHeader';
import BeneficiariesFooter from 'components/layouts/Preferences/Beneficiaries/BeneficiariesFooter';
import BeneficiaryTable from 'components/layouts/Preferences/Beneficiaries/BeneficiaryTable';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import Content, { contentConverter } from 'services/Content';
import { hasBrokerageAdvantage } from 'state/account/AccountSelectors';
import {
  getBeneficiaryTableData,
  getHasEligibleEsa
} from 'state/beneficiaries/BeneficiarySelectors';
import {
  hasIndividualOrJointTenant,
  hasROTHOrTraditionalInheritedIRABene,
  hasWorkplaceRetirementAccounts,
  hasAlaska529,
  hasMaryland529,
  hasNational529,
  hasMultipleCollegeSavings
} from 'state/view/ViewSelectors';
import { getAgreementBeneficiaries } from 'services/Rest';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { Spinner } from '@trp-beacon-ui-kit/react-communication';

class Beneficiaries extends React.Component {
  state = {
    loading: true
  };

  componentDidMount() {
    getAgreementBeneficiaries();
  }

  componentDidUpdate(prevProps) {
    const { requestStatus } = this.props;
    const { requestStatus: prevRequest } = prevProps;
    if (
      requestStatus.beneficiaries === REQUEST_STATES.received &&
      prevProps.requestStatus.beneficiaries !== REQUEST_STATES.received
    ) {
      this.setState({ loading: false });
    }

    if (
      requestStatus.updateBeneficiaries &&
      requestStatus.updateBeneficiaries === REQUEST_STATES.received &&
      prevRequest.updateBeneficiaries !== REQUEST_STATES.received
    ) {
      this.setState({ loading: true });
      getAgreementBeneficiaries();
    }
  }

  render() {
    const {
      brokerageAdvantage,
      hasEligibleEsa,
      hasIndividualOrJointTenant,
      hasROTHOrTraditionalInheritedIRABene,
      hasWorkplaceRetirementAccounts,
      tableData,
      isAlaska529,
      isMaryland529,
      isNational529,
      hasMultipleCollegeSaving
    } = this.props;
    const { loading } = this.state;

    const { beneficiaryData, retirementAccounts, nonRetirementAccounts } =
      tableData;

    const isNotQualified = isEmpty(beneficiaryData);

    const accounts = [
      {
        label: 'Retirement',
        beneficiariesByAccountType: retirementAccounts
      },
      {
        label: 'Non-Retirement',
        beneficiariesByAccountType: nonRetirementAccounts
      }
    ];

    return (
      <>
        {!loading && (
          <Fragment>
            <PreferencesHeader>
              <div className="beneficiaries-header">Beneficiaries</div>
            </PreferencesHeader>
            <PreferencesSection
              className="beneficiaries-preferences-section"
              fullBackground={true}>
              {isNotQualified ? (
                <div className="not-qualified-beneficiaries">
                  <span
                    className="not-qualified-statement"
                    dangerouslySetInnerHTML={{
                      __html: contentConverter.makeHtml(
                        Content.beneficiaryNotQualifiedDisclosure
                      )
                    }}
                  />
                  {hasWorkplaceRetirementAccounts && (
                    <span
                      className="investing-disclosure"
                      dangerouslySetInnerHTML={{
                        __html: contentConverter.makeHtml(
                          Content.beneficiaryWorkplaceRetirementDisclosure
                        )
                      }}
                    />
                  )}
                </div>
              ) : (
                <Fragment>
                  {accounts.map((account, i) => {
                    return (
                      <div key={i} className="beneficiaries-account-type-list ">
                        {account.beneficiariesByAccountType?.length ? (
                          <div
                            data-testid="retirement-non-retirement-label"
                            className="retirement-non-retirement-title">
                            {account.label}
                          </div>
                        ) : null}
                        {account.beneficiariesByAccountType.map(
                          (beneByAccountType, i) => {
                            const {
                              beneficiaries,
                              agreements,
                              beneficiariesMatch,
                              agreementTypeValue: { displayName },
                              isEditable,
                              isIraAccount
                            } = beneByAccountType;
                            return (
                              <BeneficiaryTable
                                beneficiaries={beneficiaries}
                                accounts={agreements}
                                disableAllActions={!beneficiariesMatch}
                                accountType={displayName}
                                isEditable={isEditable}
                                isIraAccount={isIraAccount}
                                key={i}
                              />
                            );
                          }
                        )}
                      </div>
                    );
                  })}
                  <BeneficiariesFooter
                    brokerageAdvantage={brokerageAdvantage}
                    hasIndividualOrJointTenant={hasIndividualOrJointTenant}
                    hasROTHOrTraditionalInheritedIRABene={
                      hasROTHOrTraditionalInheritedIRABene
                    }
                    hasWorkplaceRetirementAccounts={
                      hasWorkplaceRetirementAccounts
                    }
                    isAlaska529={isAlaska529}
                    isMaryland529={isMaryland529}
                    isNational529={isNational529}
                    hasMultipleCollegeSavings={hasMultipleCollegeSaving}
                  />
                </Fragment>
              )}
            </PreferencesSection>
            <div className="beneficiaries-legal-copy">
              <LegalCopy content={Content.beneficiaryDisclosure} />
              {hasEligibleEsa && (
                <LegalCopy content={Content.esaBeneficiaryDisclaimer} />
              )}
            </div>
          </Fragment>
        )}
        {loading && (
          <div className="center">
            <Spinner />
          </div>
        )}
      </>
    );
  }
}

Beneficiaries.defaultProps = {
  tableData: {
    beneficiaryData: [],
    retirementAccounts: [],
    nonRetirementAccounts: []
  }
};

const mapStateToProps = (state) => ({
  brokerageAdvantage: hasBrokerageAdvantage(state),
  hasEligibleEsa: getHasEligibleEsa(state),
  hasIndividualOrJointTenant: hasIndividualOrJointTenant(state),
  hasROTHOrTraditionalInheritedIRABene:
    hasROTHOrTraditionalInheritedIRABene(state),
  hasWorkplaceRetirementAccounts: hasWorkplaceRetirementAccounts(state),
  requestStatus: getRequestStatus(state),
  tableData: getBeneficiaryTableData(state),
  isAlaska529: hasAlaska529(state),
  isMaryland529: hasMaryland529(state),
  isNational529: hasNational529(state),
  hasMultipleCollegeSaving: hasMultipleCollegeSavings(state)
});

export default connect(mapStateToProps)(Beneficiaries);
